// BenchSheet — bottom sheet in the crafting view for vsj.bench. Fixed
// header (title/back/close), scrolling content region capped at 35vh
// (component squares w/ jump-to-maker, output preview + stats, utilize
// slot, material pickers as level-sorted chip flows with "+N more" ghost
// collapse), and an anchored Start / Craft 1 footer. Whenever the concrete
// recipe changes the info pane hops to the OUTPUT item's sheet.

import { clear, el, fmtDur, kbtn, tooltip } from '../kit';
import { sendEvent } from '../net';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';

export function benchSheet(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const body = el('div', { style: 'display:flex;flex-direction:column;gap:6px' });
  const root = el('div', {
    class: 'k-panel bench',
    style: 'display:none;background:var(--bg-modal);border-color:var(--border-light)',
  });
  root.append(body);

  let sig = '';
  let tpl = '';
  let recipe = '';
  const ghostOpen: Record<string, boolean> = {};

  const bench = (): any => {
    const b = vsj.at('bench');
    return b && typeof b === 'object' ? b : null;
  };

  const componentSquare = (comp: any): HTMLElement => {
    const box = el('div', { style: 'display:flex;flex-direction:column;gap:2px;align-items:center' });
    box.append(
      el('div', { class: 'k-well', style: 'width:54px;height:54px' }, icons.texture(String(comp.item ?? ''), 38)),
    );
    const short = comp.short === true; // served verdict — nothing compared here
    box.append(
      el('div', { class: 'k-small' + (short ? ' short' : ' k-dim'), text: `${comp.have ?? 0} / ${comp.count ?? 0}` }),
    );
    if (short && comp.jump) {
      // served label ("Mine Copper Ore") — the producer's verb is driver knowledge
      box.append(kbtn(comp.jump, { label: String(comp.jump_label ?? 'Make'), compact: true }));
    }
    return box;
  };

  const outputSquare = (b: any): HTMLElement => {
    const out = b.output ?? {};
    const box = el('div', { style: 'display:flex;flex-direction:column;gap:2px;align-items:center' });
    const sq = el('div', { class: 'k-well', style: 'width:62px;height:62px' });
    if (out.item) {
      sq.append(icons.texture(String(out.item), 46));
      sq.style.cursor = 'pointer';
      sq.addEventListener('click', () => ui.select(String(out.item)));
      tooltip(sq, () => "What you'll make — click for its info");
    }
    box.append(sq);
    if (out.item) {
      const count = Number(out.count ?? 1);
      box.append(
        el('div', { class: 'k-small', text: vsj.infoName(String(out.item)) + (count > 1 ? ` ×${count}` : '') }),
      );
    }
    return box;
  };

  const levelOrder = (d: Record<string, any>): string[] =>
    Object.keys(d)
      .map((k, i) => ({ k, i, lv: typeof d[k]?.level === 'number' ? d[k].level : Infinity }))
      .sort((a, b) => a.lv - b.lv || a.i - b.i)
      .map((e) => e.k);

  const buildPicker = (parent: HTMLElement, p: any) => {
    const sec = el('div', { style: 'display:flex;flex-direction:column;gap:4px' });
    parent.append(sec);
    sec.append(el('div', { class: 'k-small k-faint', text: String(p.label ?? 'Material') }));
    const flow = el('div', { class: 'chip-flow' });
    sec.append(flow);
    const current = String(p.current ?? '');
    const options = p.options ?? {};
    for (const mat of levelOrder(options)) {
      const opt = options[mat];
      if (!opt || typeof opt !== 'object') continue;
      let label = String(opt.name ?? mat);
      if (opt.units != null) label += ` (makes ${opt.units})`; // served yield
      const chip = mat === current
        ? el('button', { class: 'k-btn compact active', text: label })
        : kbtn(opt.pick, { label, compact: true });
      // served display text (bag/ground counts, pre-composed)
      tooltip(chip, () => String(opt.counts_text ?? ''));
      // served per-option effect prose (mould params) — faint line under it
      if (opt.effect_text != null)
        flow.append(
          el(
            'div',
            { style: 'display:flex;flex-direction:column;gap:1px;align-items:flex-start' },
            chip,
            el('div', { class: 'k-small k-faint', text: String(opt.effect_text) }),
          ),
        );
      else flow.append(chip);
    }
    const ghosts = p.ghosts ?? {};
    const gnames = levelOrder(ghosts);
    if (gnames.length) {
      const paramKey = String(p.param ?? 0);
      const open = !!ghostOpen[paramKey];
      const hidden: HTMLElement[] = [];
      gnames.forEach((mat, i) => {
        const lvl = ghosts[mat]?.level ?? '?';
        const gchip = el('button', {
          class: 'k-btn compact',
          style: 'opacity:0.55',
          disabled: true,
          text: `${ghosts[mat]?.name ?? mat} · Lv ${lvl}`,
        });
        (gchip as HTMLButtonElement).disabled = true;
        tooltip(gchip, () => `Unowned — craftable at Lv ${lvl}`);
        if (!open && i >= 2) {
          gchip.style.display = 'none';
          hidden.push(gchip);
        }
        flow.append(gchip);
      });
      if (hidden.length) {
        const more = el('button', { class: 'k-btn compact', style: 'opacity:0.55', text: `+${hidden.length} more` });
        tooltip(more, () => 'Show the farther locked tiers');
        more.addEventListener('click', () => {
          ghostOpen[paramKey] = true;
          hidden.forEach((c) => (c.style.display = ''));
          more.style.display = 'none';
        });
        flow.append(more);
      }
    }
  };

  const openToolPopover = (anchor: HTMLElement) => {
    const u = bench()?.utilize;
    if (!u || typeof u !== 'object') return;
    document.getElementById('bench-tool-pop')?.remove();
    const rows = el('div', { style: 'display:flex;flex-direction:column;gap:4px' });
    rows.append(el('div', { class: 'k-small k-faint', text: 'Use which tool?' }));
    const options = u.options ?? {};
    const names = Object.keys(options).sort();
    if (!names.length) rows.append(el('div', { class: 'k-small k-dim', text: 'No qualifying tools.' }));
    for (const item of names) {
      const opt = options[item];
      if (!opt || typeof opt !== 'object') continue;
      const pick = kbtn(opt.pick, { label: 'Pick', compact: true, onFired: () => pop.remove() });
      rows.append(
        el(
          'div',
          { style: 'display:flex;gap:6px;align-items:center' },
          icons.texture(item, 20),
          el('span', {
            class: 'k-small',
            style: `flex:1;color:${String(u.current) === item ? 'var(--text)' : 'var(--text-dim)'}`,
            text: vsj.infoName(item),
          }),
          el('span', { class: 'k-small k-faint', text: String(opt.from ?? '') }),
          pick,
        ),
      );
    }
    const pop = el('div', {
      id: 'bench-tool-pop',
      class: 'k-panel',
      style:
        'position:fixed;z-index:70;width:280px;padding:10px;background:var(--bg-modal);' +
        'border-color:var(--border-light)',
    });
    pop.append(rows);
    document.body.append(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.left = `${Math.min(r.left, innerWidth - 300)}px`;
    pop.style.top = `${Math.max(8, r.top - pop.offsetHeight - 6)}px`;
    const dismiss = (e: PointerEvent) => {
      if (!pop.contains(e.target as Node)) {
        pop.remove();
        document.removeEventListener('pointerdown', dismiss);
      }
    };
    setTimeout(() => document.addEventListener('pointerdown', dismiss), 0);
  };

  const refresh = () => {
    const b = bench();
    root.style.display = b ? 'flex' : 'none';
    if (!b) {
      sig = '';
      return;
    }
    const newTpl = String(b.template ?? '');
    if (newTpl !== tpl) {
      tpl = newTpl;
      for (const k of Object.keys(ghostOpen)) delete ghostOpen[k];
    }
    // recipe change (open / pick / jump) hops the info pane to the output item
    const newRecipe = String(b.recipe ?? '');
    if (newRecipe !== recipe) {
      recipe = newRecipe;
      if (newRecipe && b.output?.item) ui.select(String(b.output.item));
    }
    const glow: string[] = vsj.at('guidance.glow') ?? [];
    const guided = glow.includes('card:' + newTpl);
    const s =
      JSON.stringify(b) + (guided ? '!c' : '') +
      (glow.includes('btn:start') ? '!s' : '') + (glow.includes('btn:craft_one') ? '!1' : '');
    if (s === sig) return;
    sig = s;
    clear(body);

    // header
    const head = el('div', { style: 'display:flex;gap:6px;align-items:center' });
    if (b.back && b.back._event_id) {
      const backBtn = kbtn(b.back, { label: String(b.back.label ?? '‹ Back') });
      tooltip(backBtn, () => 'Back to the bench you jumped from');
      head.append(backBtn);
    }
    head.append(
      el('div', { class: 'k-title', style: 'flex:1', text: `Bench — ${b.name ?? b.template ?? ''}` }),
      el('button', { class: 'k-btn compact bench-close', text: '✕', onclick: () => sendEvent('close_bench') }),
    );
    body.append(head);

    // scrolling content
    const content = el('div', { class: 'bench-body k-scroll' });
    body.append(content);
    const cols = el('div', { style: 'display:flex;gap:20px;flex-wrap:wrap' });
    content.append(cols);

    const compCol = el('div', { style: 'display:flex;flex-direction:column;gap:4px' });
    cols.append(compCol);
    compCol.append(el('div', { class: 'k-small k-faint', text: 'Components' }));
    const compRow = el('div', { style: 'display:flex;gap:6px;align-items:center' });
    compCol.append(compRow);
    if (Array.isArray(b.components))
      for (const comp of b.components)
        if (comp && typeof comp === 'object') compRow.append(componentSquare(comp));
    compRow.append(el('span', { style: 'font-size:var(--font-title);color:var(--text-faint)', text: '→' }));
    compRow.append(outputSquare(b));

    const stats = b.stats ?? {};
    if (stats && typeof stats === 'object' && Object.keys(stats).length) {
      const st = el('div', { style: 'display:flex;gap:10px;flex-wrap:wrap' });
      compCol.append(st);
      // served real_s (your actual time) leads; the base time is the faint
      // fallback — mirrors the card's "Xs (base Ys)" pattern
      const realS = b.real_s ?? stats.real_s;
      const fmt: Record<string, (v: any) => string> = {
        skill: (v) => cap(String(v)),
        level: (v) => `Lv ${v}`,
        time_s: (v) =>
          realS != null && Math.abs(Number(realS) - Number(v)) > 0.05
            ? `${fmtDur(Number(realS))} (base ${fmtDur(Number(v))})`
            : fmtDur(Number(realS ?? v)),
        xp: (v) => `+${v} xp`,
        durability: (v) => `dur ${v}`,
        tier: (v) => `T${v}`,
      };
      for (const k of ['skill', 'level', 'time_s', 'xp', 'durability', 'tier'])
        if (stats[k] != null) st.append(el('span', { class: 'k-small k-dim', text: fmt[k](stats[k]) }));
    }

    const utilize = b.utilize;
    if (utilize && typeof utilize === 'object') {
      const uCol = el('div', { style: 'display:flex;flex-direction:column;gap:4px;align-items:flex-start' });
      cols.append(uCol);
      uCol.append(el('div', { class: 'k-small k-faint', text: 'Utilize' }));
      const cur = utilize.current;
      const uBtn = el('button', { class: 'k-btn', style: 'width:54px;height:54px' });
      if (cur != null) uBtn.append(icons.texture(String(cur), 38));
      else uBtn.textContent = '—';
      tooltip(uBtn, () => 'Pick which tool to use');
      uBtn.addEventListener('click', () => openToolPopover(uBtn));
      uCol.append(uBtn);
      if (cur != null) uCol.append(el('div', { class: 'k-small k-dim', text: vsj.infoName(String(cur)) }));
    }

    if (Array.isArray(b.pickers))
      for (const p of b.pickers) if (p && typeof p === 'object') buildPicker(content, p);

    // anchored footer
    const reason = b.blocked != null ? String(b.blocked) : null;
    const startBtn = kbtn(b.start, {
      label: 'Start',
      denyReason: reason,
      glow: guided && glow.includes('btn:start') && !glow.includes('btn:craft_one'),
    });
    tooltip(startBtn, () => 'Craft until stopped');
    const oneBtn = kbtn(b.craft_one, {
      label: 'Craft 1',
      denyReason: reason,
      glow: guided && glow.includes('btn:craft_one'),
    });
    body.append(el('div', { class: 'bench-footer' }, startBtn, oneBtn));
  };

  refresh();
  return {
    root,
    refresh(changed) {
      if (changed.has('bench') || changed.has('guidance') || changed.has('infos') || changed.has('activities') || changed.has('*'))
        refresh();
    },
  };
}

function cap(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
